/**
 * Tickets Store
 * Manages ticket upload and status tracking
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  Ticket,
  TicketStatus,
  TicketFilters,
  TicketUploadProgress,
  MultiCampaignUploadResult
} from '@/types'
import { ticketService } from '@/services/api'
import { useAuthStore } from './auth'
import { t } from '@/i18n'

export const useTicketsStore = defineStore('tickets', () => {
  // State
  const tickets = ref<Ticket[]>([])
  const recentTickets = ref<Ticket[]>([])
  const selectedTicket = ref<Ticket | null>(null)
  const filters = ref<TicketFilters>({
    status: 'all',
    challengeId: undefined,
  })
  const uploadProgress = ref<TicketUploadProgress | null>(null)
  const ticketCounts = ref<Record<TicketStatus | 'total', number>>({
    approved: 0,
    in_review: 0,
    declined: 0,
    total: 0,
  })
  const isLoading = ref(false)
  const isUploading = ref(false)
  const error = ref<string | null>(null)
  const pagination = ref({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  })

  // Getters
  const approvedTickets = computed(() =>
    tickets.value.filter(t => t.status === 'approved')
  )

  const pendingTickets = computed(() =>
    tickets.value.filter(t => t.status === 'in_review')
  )

  const declinedTickets = computed(() =>
    tickets.value.filter(t => t.status === 'declined')
  )

  const hasMorePages = computed(() =>
    pagination.value.page < pagination.value.totalPages
  )

  const uploadInProgress = computed(() =>
    uploadProgress.value?.status === 'uploading' ||
    uploadProgress.value?.status === 'processing'
  )

  // Actions
  async function fetchTickets(page: number = 1, append: boolean = false) {
    const authStore = useAuthStore()
    if (!authStore.user) return

    isLoading.value = true
    error.value = null

    try {
      const response = await ticketService.getTickets(
        authStore.user.id,
        filters.value,
        page,
        pagination.value.pageSize
      )

      if (append) {
        tickets.value = [...tickets.value, ...response.data]
      } else {
        tickets.value = response.data
      }

      pagination.value = {
        page: response.page,
        pageSize: response.pageSize,
        total: response.total,
        totalPages: response.totalPages,
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.loadTickets')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function fetchRecentTickets(limit: number = 5) {
    const authStore = useAuthStore()
    if (!authStore.user) return

    try {
      recentTickets.value = await ticketService.getRecentTickets(
        authStore.user.id,
        limit
      )
    } catch (e) {
      console.error('Error fetching recent tickets:', e)
    }
  }

  async function fetchTicketCounts() {
    const authStore = useAuthStore()
    if (!authStore.user) return

    try {
      ticketCounts.value = await ticketService.getTicketCounts(authStore.user.id)
    } catch (e) {
      console.error('Error fetching ticket counts:', e)
    }
  }

  async function fetchTicket(id: string) {
    isLoading.value = true
    error.value = null

    try {
      selectedTicket.value = await ticketService.getTicket(id)
      return selectedTicket.value
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.loadTicket')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function fetchTicketsByStatus(status: TicketStatus) {
    const authStore = useAuthStore()
    if (!authStore.user) return []

    isLoading.value = true
    error.value = null

    try {
      return await ticketService.getTicketsByStatus(authStore.user.id, status)
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.loadTickets')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function uploadTicket(
    challengeId: string,
    challengeTitle: string,
    file: File
  ) {
    const result = await uploadTicketBatch([challengeId], file, challengeTitle)
    const first = result.tickets[0]
    if (!first) {
      throw new Error(t('errors.noTicketCreated'))
    }
    return first
  }

  async function uploadTicketBatch(
    challengeIds: string[],
    file: File,
    _contextTitle?: string
  ): Promise<MultiCampaignUploadResult> {
    const authStore = useAuthStore()
    if (!authStore.user) throw new Error(t('errors.noActiveSession'))

    isUploading.value = true
    error.value = null
    uploadProgress.value = {
      fileKey: '',
      progress: 0,
      status: 'pending',
    }

    try {
      uploadProgress.value.status = 'uploading'

      const result = await ticketService.uploadTicketBatch(
        challengeIds,
        file,
        (progress) => {
          if (uploadProgress.value) {
            uploadProgress.value.progress = progress
            if (progress === 100) {
              uploadProgress.value.status = 'processing'
            }
          }
        }
      )

      uploadProgress.value.status = 'completed'

      // Add to tickets list
      const createdTickets = result.tickets
      tickets.value = [...createdTickets, ...tickets.value]
      recentTickets.value = [...createdTickets, ...recentTickets.value].slice(0, 5)

      for (const ticket of createdTickets) {
        ticketCounts.value[ticket.status] = (ticketCounts.value[ticket.status] || 0) + 1
        ticketCounts.value.total++
      }

      return result
    } catch (e) {
      const rawMessage = e instanceof Error ? e.message : t('errors.uploadTicket')
      error.value =
        rawMessage === 'Failed to create ticket batch'
          ? t('errors.ticketBatchFailed')
          : rawMessage
      if (uploadProgress.value) {
        uploadProgress.value.status = 'error'
        uploadProgress.value.errorMessage = error.value
      }
      throw e
    } finally {
      isUploading.value = false
    }
  }

  function setStatusFilter(status: 'all' | TicketStatus) {
    filters.value.status = status
    pagination.value.page = 1
    fetchTickets()
  }

  function setChallengeFilter(challengeId: string | undefined) {
    filters.value.challengeId = challengeId
    pagination.value.page = 1
    fetchTickets()
  }

  function clearFilters() {
    filters.value = {
      status: 'all',
      challengeId: undefined,
    }
    pagination.value.page = 1
    fetchTickets()
  }

  async function loadMore() {
    if (!hasMorePages.value || isLoading.value) return
    await fetchTickets(pagination.value.page + 1, true)
  }

  function selectTicket(ticket: Ticket | null) {
    selectedTicket.value = ticket
  }

  function clearUploadProgress() {
    uploadProgress.value = null
  }

  function clearError() {
    error.value = null
  }

  return {
    // State
    tickets,
    recentTickets,
    selectedTicket,
    filters,
    uploadProgress,
    ticketCounts,
    isLoading,
    isUploading,
    error,
    pagination,

    // Getters
    approvedTickets,
    pendingTickets,
    declinedTickets,
    hasMorePages,
    uploadInProgress,

    // Actions
    fetchTickets,
    fetchRecentTickets,
    fetchTicketCounts,
    fetchTicket,
    fetchTicketsByStatus,
    uploadTicketBatch,
    uploadTicket,
    setStatusFilter,
    setChallengeFilter,
    clearFilters,
    loadMore,
    selectTicket,
    clearUploadProgress,
    clearError,
  }
})
